import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { getToken as getStoredToken } from "../../lib/authStorage";
import { BACKEND_URL } from "../../lib/backendUrl";
import { downloadBillPdf } from "../../lib/billDownload";
import CustomerPicker from "../../components/CustomerPicker";
import { formatINR, formatDate, todayISO } from "../../lib/utils.fmt";
import { shiftIST, addDaysISO } from "../../lib/istDate";
import { Truck, Plus, Box, Film, BarChart3, Users, MapPin, CheckCircle2, XCircle, Calendar, Trash2, Pencil, Power, UserX, Search, ChevronUp, ChevronDown, X, Package, Percent, MessageSquare, ShieldCheck, AlertTriangle } from "lucide-react";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import CustomerUnit from "../../components/CustomerUnit";
import { cancelNote, statusBreakdown } from "../../lib/deliveriesView";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { LineChart, Line, BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";
// Per-variant pack-summary items (one product can have a 1 L and a 0.5 L line).
import { packItemKey } from "../../lib/riderLabels";
import { filterableRoutes, routeOptionLabel } from "../../lib/routeOptions";

// ============ DELIVERIES ============
export function AdminDeliveries() {
  const { tenant } = useAuth();
  // Mirror the tenant-wide bottle toggle — hides the Bottles column on this
  // page and the "Bottles returned (avg)" input on the bulk-deliver dialog.
  const bottlesOn = tenant?.bottle_tracking_enabled !== false;
  const [deliveries, setDeliveries] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [bulkBottles, setBulkBottles] = useState(1);
  const [bulkStatus, setBulkStatus] = useState("delivered");
  // Filters & search
  const [q, setQ] = useState("");
  const [routeFilter, setRouteFilter] = useState("__all__");
  const [cityFilter, setCityFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [sortBy, setSortBy] = useState({ key: "customer_name", dir: "asc" });
  // Pack Summary state — aggregated per-product totals for current filter set.
  const [packOpen, setPackOpen] = useState(false);
  const [packData, setPackData] = useState(null);
  const [packBusy, setPackBusy] = useState(false);
  // Off = "what's left to pack" (default). On = full day total including
  // delivered/failed/skipped rows, for an end-of-day product tally.
  const [packIncludeCompleted, setPackIncludeCompleted] = useState(false);

  const fetchPack = async ({ slot, includeCompleted } = {}) => {
    setPackBusy(true);
    const params = new URLSearchParams({ date });
    if (routeFilter !== "__all__" && routeFilter !== "__unassigned__") params.set("route_id", routeFilter);
    if (slot) params.set("slot", slot);
    if (includeCompleted) params.set("include_completed", "true");
    try {
      const { data } = await api.get(`/api/admin/deliveries/pack-summary?${params.toString()}`);
      setPackData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pack summary failed");
      setPackData(null);
    } finally { setPackBusy(false); }
  };

  const openPack = async () => {
    setPackOpen(true);
    await fetchPack({ includeCompleted: packIncludeCompleted });
  };

  // Per-slot drill from the dialog itself.
  const reloadPack = (slot) => fetchPack({ slot, includeCompleted: packIncludeCompleted });

  const togglePackIncludeCompleted = () => {
    const next = !packIncludeCompleted;
    setPackIncludeCompleted(next);
    const currentSlot = packData && packData.slot !== "all" ? packData.slot : undefined;
    fetchPack({ slot: currentSlot, includeCompleted: next });
  };

  // Loads are numbered and only the newest may change the list: a slower
  // answer to an earlier request — a date the admin has already left
  // (Yesterday → Today in quick succession), or a reload overtaken by the
  // next one — is dropped. The newest load also ends the loading state,
  // whoever started it.
  const loadSeq = useRef(0);
  const load = () => {
    setLoadError(null);
    const mine = ++loadSeq.current;
    return api.get(`/api/admin/deliveries?date=${date}`).then(({ data }) => {
      if (loadSeq.current !== mine) return;
      setDeliveries(data.deliveries); setSelected([]); setLoading(false);
    }).catch((e) => {
      if (loadSeq.current !== mine) return;
      setLoadError(friendlyMessage(e)); toast.error(friendlyMessage(e)); setLoading(false);
    });
  };
  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  useEffect(() => {
    api.get("/api/admin/routes").then(({ data }) => setRoutes(data.routes || [])).catch((e) => toast.error(friendlyMessage(e)));
  }, []);

  const isOpenStatus = (s) => s === "pending" || s === "confirmed" || s === "out_for_delivery";
  const pendingOnPage = deliveries.filter((d) => isOpenStatus(d.status));
  const allSelected = pendingOnPage.length > 0 && selected.length === pendingOnPage.length;

  const toggleAll = () => {
    setSelected(allSelected ? [] : pendingOnPage.map((d) => d.id));
  };

  const toggleOne = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const bulkMark = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/deliveries/bulk-mark", {
        ids: selected, status: bulkStatus,
        bottles_returned_count: bottlesOn && bulkStatus === "delivered" ? bulkBottles : 0,
      });
      const verb = ({ delivered: "delivered", failed: "marked failed", skipped: "skipped", out_for_delivery: "marked out for delivery" })[bulkStatus] || "updated";
      toast.success(`${data.updated} deliveries ${verb}`);
      setOpen(false); setBulkBottles(1); setBulkStatus("delivered");
      load();
    } catch (e) {
      toast.error("Bulk mark failed");
    } finally { setBusy(false); }
  };

  const markRow = async (d, status) => {
    if (!window.confirm(`Mark this ${d.product_name} delivery to ${d.customer_name} as ${status}?`)) return;
    try {
      const { data } = await api.post("/api/admin/deliveries/bulk-mark", {
        ids: [d.id], status, bottles_returned_count: 0,
      });
      toast.success(`Marked ${status} · ${data.updated || 1} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark failed");
    }
  };

  const statusPill = (s) => ({
    placed: "bg-neutral-100 text-neutral-700",
    pending: "bg-neutral-100 text-neutral-700",
    confirmed: "bg-brand-leaf/10 text-brand-leaf",
    out_for_delivery: "bg-brand-sky/10 text-brand-sky",
    delivered: "bg-brand-leaf/10 text-brand-leaf",
    failed: "bg-brand-clay/10 text-brand-clay",
    skipped: "bg-neutral-100 text-neutral-500",
  })[s] || "bg-neutral-100";

  const cities = Array.from(new Set(deliveries.map((d) => d.customer_city).filter(Boolean))).sort();

  const passesOtherFilters = (d) => {
    if (q.trim()) {
      const needle = q.toLowerCase();
      if (!(d.customer_name || "").toLowerCase().includes(needle) &&
          !(d.product_name || "").toLowerCase().includes(needle)) return false;
    }
    if (routeFilter === "__unassigned__" && d.route_id) return false;
    if (routeFilter !== "__all__" && routeFilter !== "__unassigned__" && d.route_id !== routeFilter) return false;
    if (cityFilter !== "__all__" && (d.customer_city || "") !== cityFilter) return false;
    return true;
  };
  const visible = deliveries.filter((d) => passesOtherFilters(d) && (statusFilter === "__all__" || d.status === statusFilter));
  // An empty list under a status filter: what the other filters DO match,
  // e.g. "49 cancelled · 1 placed" for a route whose day was cancelled.
  const otherStatuses = statusFilter !== "__all__" && visible.length === 0
    ? statusBreakdown(deliveries.filter(passesOtherFilters)) : [];
  const statusWord = (s) => (s || "").replace(/_/g, " ");
  const emptyState = (testId) => otherStatuses.length > 0 ? (
    <EmptyState icon={Truck} size="sm" testId={testId}
      title={`No ${statusWord(statusFilter)} deliveries for these filters`}
      description={<span data-testid="del-empty-breakdown">On this date they have {otherStatuses.map(([s, n]) => `${n} ${statusWord(s)}`).join(" · ")}.</span>}
      action={<button onClick={() => setStatusFilter("__all__")} className="h-9 px-4 rounded-full bg-brand-sky text-white text-xs font-bold" data-testid="del-empty-show-all">Show all statuses</button>} />
  ) : <EmptyState icon={Truck} size="sm" title="No deliveries match the filters" testId={testId} />;

  const toggleSort = (key) => setSortBy((p) => p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortHeader = ({ k, children, align = "left" }) => (
    <th className={`text-${align} px-5 py-3 select-none cursor-pointer`} onClick={() => toggleSort(k)} data-testid={`del-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}{sortBy.key === k ? (sortBy.dir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>) : null}</span>
    </th>
  );

  const sorted = [...visible].sort((a, b) => {
    const k = sortBy.key;
    const get = (x) => {
      switch (k) {
        case "sequence": return x.sequence || 0;
        case "customer_name": return (x.customer_name || "").toLowerCase();
        case "route_name": return (x.route_name || "~zzz").toLowerCase();
        case "rider_name": return (x.rider_name || "~zzz").toLowerCase();
        case "city": return (x.customer_city || "~zzz").toLowerCase();
        case "product_name": return (x.product_name || "").toLowerCase();
        case "product_brand": return (x.product_brand || "").toLowerCase();
        case "product_category": return (x.product_category || "").toLowerCase();
        case "slot": return x.slot || "";
        case "quantity": return x.quantity || 0;
        case "amount": return x.amount || 0;
        case "status": return x.status || "";
        default: return "";
      }
    };
    const av = get(a), bv = get(b);
    if (typeof av === "number" && typeof bv === "number") return sortBy.dir === "asc" ? av - bv : bv - av;
    if (av < bv) return sortBy.dir === "asc" ? -1 : 1;
    if (av > bv) return sortBy.dir === "asc" ? 1 : -1;
    return 0;
  });

  // IST calendar days, same clock as todayISO() — the UTC date lags a day
  // between 00:00 and 05:30 IST.
  const DATE_CHIPS = [
    { v: shiftIST(-1), label: "Yesterday", testid: "del-shortcut-yesterday" },
    { v: todayISO(), label: "Today", testid: "del-shortcut-today" },
    { v: shiftIST(1), label: "Tomorrow", testid: "del-shortcut-tomorrow" },
  ];

  return (
    <div className="space-y-6 page-enter" data-testid="admin-deliveries-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Operations</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Deliveries ({deliveries.length})</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {DATE_CHIPS.map((c) => (
            <button key={c.v} onClick={() => setDate(c.v)}
                    className={`h-10 px-3 rounded-full text-xs font-bold ${date === c.v ? "bg-brand-ink text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                    data-testid={c.testid}>{c.label}</button>
          ))}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 px-3 rounded-2xl border border-neutral-200 bg-white text-sm" data-testid="date-picker" />
          <Button onClick={openPack} className="h-10 rounded-2xl bg-brand-ink hover:bg-neutral-800 text-white font-bold px-3" data-testid="admin-pack-summary-btn">
            <Package className="w-4 h-4 mr-1"/> <span className="hidden sm:inline">Pack Summary</span><span className="sm:hidden">Pack</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer or product…"
                 className="pl-10 h-10 rounded-full w-full sm:w-72" data-testid="del-search-input"/>
        </div>
        <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="del-route-filter">
          <option value="__all__">All routes</option>
          <option value="__unassigned__">Unassigned</option>
          {filterableRoutes(routes).map((r) => <option key={r.id} value={r.id}>{routeOptionLabel(r)}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="del-city-filter">
          <option value="__all__">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="del-status-filter">
          <option value="__all__">All statuses</option>
          <option value="placed">Placed</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="out_for_delivery">Out for delivery</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
          <option value="cancelled">Cancelled</option>
        </select>
        {(routeFilter !== "__all__" || cityFilter !== "__all__" || statusFilter !== "__all__" || q) && (
          <button onClick={() => { setRouteFilter("__all__"); setCityFilter("__all__"); setStatusFilter("__all__"); setQ(""); }}
                  className="h-10 px-3 rounded-full text-xs font-bold bg-brand-clay/10 text-brand-clay inline-flex items-center gap-1"
                  data-testid="del-clear-filters">
            <X className="w-3 h-3"/> Clear
          </button>
        )}
        <span className="text-xs text-neutral-500 ml-auto" data-testid="del-count">{sorted.length} of {deliveries.length}</span>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="rounded-3xl bg-brand-ink text-white p-4 flex items-center justify-between gap-3 sticky top-2 z-20 shadow-lg" data-testid="bulk-action-bar">
          <div className="font-bold text-sm">
            {selected.length} selected
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-end">
            <button onClick={() => setSelected([])} className="px-4 h-10 rounded-2xl bg-white/10 font-bold text-sm" data-testid="bulk-clear">Clear</button>
            <Button onClick={() => { setBulkStatus("out_for_delivery"); setOpen(true); }} className="h-10 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-3" data-testid="bulk-out-btn">
              <Truck className="w-4 h-4 mr-1" /> Mark Out
            </Button>
            <Button onClick={() => { setBulkStatus("skipped"); setOpen(true); }} className="h-10 rounded-2xl bg-white/10 hover:bg-white/20 font-bold px-3" data-testid="bulk-skip-btn">
              Skip
            </Button>
            <Button onClick={() => { setBulkStatus("failed"); setOpen(true); }} className="h-10 rounded-2xl bg-brand-clay hover:bg-red-700 font-bold px-3" data-testid="bulk-fail-btn">
              <XCircle className="w-4 h-4 mr-1" /> Fail
            </Button>
            <Button onClick={() => { setBulkStatus("delivered"); setOpen(true); }} className="h-10 rounded-2xl bg-brand-leaf hover:bg-green-700 font-bold px-4" data-testid="bulk-mark-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Delivered
            </Button>
          </div>
        </div>
      )}

      {loading && <LoadingState />}

      {!loading && loadError && (
        <EmptyState icon={AlertTriangle} title="Couldn't load deliveries" description={loadError}
          action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>} />
      )}

      {!loading && !loadError && <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        {/* Mobile card list — visible only on screens narrower than md (768px). */}
        <ul className="md:hidden divide-y divide-neutral-100" data-testid="del-mobile-list">
          {sorted.length === 0 && (
            <li data-testid="del-mobile-empty">{emptyState("del-mobile-empty-state")}</li>
          )}
          {sorted.map((d) => {
            const canSelect = isOpenStatus(d.status);
            const isChecked = selected.includes(d.id);
            return (
              <li key={d.id} data-testid={`del-mobile-${d.id}`} className={`p-4 ${isChecked ? "bg-brand-sky/5" : ""}`}>
                <div className="flex items-start gap-3">
                  {canSelect ? (
                    <input type="checkbox" checked={isChecked} onChange={() => toggleOne(d.id)}
                           className="w-5 h-5 mt-1 accent-brand-sky shrink-0" data-testid={`select-${d.id}`}/>
                  ) : (
                    <span className="w-5 h-5 mt-1 inline-flex items-center justify-center text-[10px] text-neutral-400 font-bold shrink-0">#{d.sequence}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-bold text-base truncate">{d.customer_name}</span>
                      {d.order_type === "one_time" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-[9px] font-extrabold uppercase tracking-wider">One-time</span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusPill(d.status)}`}>{d.status.replace("_", " ")}</span>
                      {d.notes && (
                        <span title={d.notes} className="text-neutral-400" data-testid={`del-remarks-mobile-${d.id}`}>
                          <MessageSquare className="w-3.5 h-3.5"/>
                        </span>
                      )}
                    </div>
                    <CustomerUnit address={d.address} requireFlat={tenant?.require_flat_number === true} className="mt-0.5" testId={`del-unit-${d.id}`}/>
                    <div className="text-sm text-neutral-700 mt-1">
                      {d.product_name} <span className="text-neutral-400">·</span> <span className="capitalize">{d.slot}</span> <span className="text-neutral-400">·</span> <span className="font-mono font-bold">×{d.quantity}</span> <span className="text-neutral-400">·</span> <span className="font-mono font-bold">{formatINR(d.amount)}</span>
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
                      {d.customer_city || "—"} {d.route_name && <span className="ml-1 text-brand-leaf font-bold uppercase">· {d.route_name}</span>}
                      {d.rider_name && <span className="ml-1">· Rider: {d.rider_name}</span>}
                    </div>
                    {cancelNote(d) && <div className="text-[11px] text-neutral-500 mt-0.5" data-testid={`del-cancel-note-mobile-${d.id}`}>{cancelNote(d)}</div>}
                    {isOpenStatus(d.status) && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        <button onClick={() => markRow(d, "delivered")}
                                className="h-8 px-2.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold inline-flex items-center gap-1"
                                data-testid={`mark-delivered-${d.id}`}>
                          <CheckCircle2 className="w-3 h-3"/> Delivered
                        </button>
                        {(d.status === "pending" || d.status === "confirmed") && (
                          <button onClick={() => markRow(d, "out_for_delivery")}
                                  className="h-8 px-2.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-bold inline-flex items-center gap-1"
                                  data-testid={`mark-out-${d.id}`}>
                            <Truck className="w-3 h-3"/> Out
                          </button>
                        )}
                        <button onClick={() => markRow(d, "skipped")}
                                className="h-8 px-2 rounded-full bg-neutral-100 text-neutral-600 text-[10px] font-bold"
                                data-testid={`mark-skipped-${d.id}`}>Skip</button>
                        <button onClick={() => markRow(d, "failed")}
                                className="h-8 px-2 rounded-full bg-brand-clay/10 text-brand-clay text-[10px] font-bold inline-flex items-center gap-1"
                                data-testid={`mark-failed-${d.id}`}>
                          <XCircle className="w-3 h-3"/> Fail
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Desktop / tablet table — hidden on mobile so the giant min-w
            doesn't push the page into horizontal scroll. */}
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
            <tr>
              <th className="px-3 py-3 w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                       className="w-4 h-4 accent-brand-sky cursor-pointer" data-testid="select-all-checkbox"/>
              </th>
              <SortHeader k="sequence">#</SortHeader>
              <SortHeader k="customer_name">Customer</SortHeader>
              <SortHeader k="route_name">Route</SortHeader>
              <SortHeader k="rider_name">Rider</SortHeader>
              <SortHeader k="city">City</SortHeader>
              <SortHeader k="product_name">Product</SortHeader>
              <SortHeader k="product_brand">Brand</SortHeader>
              <SortHeader k="product_category">Category</SortHeader>
              <SortHeader k="slot">Slot</SortHeader>
              <SortHeader k="quantity" align="right">Qty</SortHeader>
              <SortHeader k="amount" align="right">Amount</SortHeader>
              {bottlesOn && <th className="text-left px-5 py-3">Bottles</th>}
              <SortHeader k="status">Status</SortHeader>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {sorted.map((d) => {
              const canSelect = isOpenStatus(d.status);
              const isChecked = selected.includes(d.id);
              return (
                <tr key={d.id} data-testid={`delivery-row-${d.id}`} className={`hover:bg-neutral-50 ${isChecked ? "bg-brand-sky/5" : ""}`}>
                  <td className="px-3 py-3">
                    {canSelect && (
                      <input type="checkbox" checked={isChecked} onChange={() => toggleOne(d.id)}
                             className="w-4 h-4 accent-brand-sky cursor-pointer" data-testid={`select-${d.id}`}/>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-neutral-500">{d.sequence}</td>
                  <td className="px-5 py-3 font-semibold">
                    <div className="flex items-center gap-1.5">
                      <span>{d.customer_name}</span>
                      {d.order_type === "one_time" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-brand-sand/15 text-brand-sand text-[9px] font-extrabold uppercase tracking-wider" data-testid={`one-time-flag-${d.id}`}>One-time</span>
                      )}
                    </div>
                    <CustomerUnit address={d.address} requireFlat={tenant?.require_flat_number === true} className="font-normal max-w-[220px]" testId={`del-unit-row-${d.id}`}/>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    {d.route_name ? (
                      <span className="px-2 py-0.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold uppercase tracking-wider" data-testid={`del-route-${d.id}`}>{d.route_name}</span>
                    ) : <span className="text-neutral-400 text-xs">—</span>}
                  </td>
                  <td className="px-5 py-3 text-sm" data-testid={`del-rider-${d.id}`}>
                    {d.rider_name ? (
                      <div>
                        <div className="font-semibold leading-tight truncate max-w-[140px]" title={d.rider_name}>{d.rider_name}</div>
                        {d.rider_phone && <div className="text-[10px] text-neutral-500 font-mono">{d.rider_phone}</div>}
                      </div>
                    ) : <span className="text-neutral-400 text-xs">— unassigned</span>}
                  </td>
                  <td className="px-5 py-3 text-sm">{d.customer_city || "—"}</td>
                  <td className="px-5 py-3">{d.product_name}</td>
                  <td className="px-5 py-3 text-sm" data-testid={`del-brand-${d.id}`}>{d.product_brand || "—"}</td>
                  <td className="px-5 py-3 text-sm" data-testid={`del-category-${d.id}`}>{d.product_category || "—"}</td>
                  <td className="px-5 py-3 capitalize">{d.slot}</td>
                  <td className="px-5 py-3 text-right">{d.quantity}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold">{formatINR(d.amount)}</td>
                  {bottlesOn && (
                    <td className="px-5 py-3">{d.bottles_returned_count > 0 ? `🍶 ${d.bottles_returned_count}` : "—"}</td>
                  )}
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${statusPill(d.status)}`}>{d.status.replace("_", " ")}</span>
                    {d.notes && (
                      <span title={d.notes} className="ml-1.5 text-neutral-400 hover:text-neutral-600 cursor-help" data-testid={`del-remarks-${d.id}`}>
                        <MessageSquare className="w-3.5 h-3.5 inline"/>
                      </span>
                    )}
                    {cancelNote(d) && <div className="text-[10px] text-neutral-500 mt-1 max-w-[200px]" data-testid={`del-cancel-note-${d.id}`}>{cancelNote(d)}</div>}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {isOpenStatus(d.status) ? (
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        <button onClick={() => markRow(d, "delivered")}
                                className="h-8 px-2.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold inline-flex items-center gap-1"
                                data-testid={`mark-delivered-${d.id}`}>
                          <CheckCircle2 className="w-3 h-3"/> Delivered
                        </button>
                        {(d.status === "pending" || d.status === "confirmed") && (
                          <button onClick={() => markRow(d, "out_for_delivery")}
                                  className="h-8 px-2.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-bold inline-flex items-center gap-1"
                                  data-testid={`mark-out-${d.id}`}>
                            <Truck className="w-3 h-3"/> Out
                          </button>
                        )}
                        <button onClick={() => markRow(d, "skipped")}
                                className="h-8 px-2 rounded-full bg-neutral-100 text-neutral-600 text-[10px] font-bold inline-flex items-center gap-1"
                                data-testid={`mark-skipped-${d.id}`}
                                title="Skipped">
                          Skip
                        </button>
                        <button onClick={() => markRow(d, "failed")}
                                className="h-8 px-2 rounded-full bg-brand-clay/10 text-brand-clay text-[10px] font-bold inline-flex items-center gap-1"
                                data-testid={`mark-failed-${d.id}`}
                                title="Failed">
                          <XCircle className="w-3 h-3"/>
                        </button>
                      </div>
                    ) : <span className="text-neutral-400 text-xs">—</span>}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && <tr><td colSpan="15">{emptyState("del-empty")}</td></tr>}
          </tbody>
        </table>
        </div>
      </div>}

      {/* Bulk confirm dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {bulkStatus === "delivered" && `Mark ${selected.length} as delivered?`}
              {bulkStatus === "failed" && `Mark ${selected.length} as failed?`}
              {bulkStatus === "skipped" && `Mark ${selected.length} as skipped?`}
              {bulkStatus === "out_for_delivery" && `Mark ${selected.length} as out for delivery?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            {bulkStatus === "delivered" && `Wallets will be debited per delivery${bottlesOn ? " and bottle counters updated" : ""}.`}
            {bulkStatus === "failed" && "No wallet debit. Riders can still re-attempt manually."}
            {bulkStatus === "skipped" && "Delivery is skipped for today. No wallet debit."}
            {bulkStatus === "out_for_delivery" && "Marks selected deliveries as actively in transit. No wallet debit."}
          </p>
          {bottlesOn && bulkStatus === "delivered" && (
            <>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mt-2 block">Bottles returned (per delivery, avg)</label>
              <Input type="number" min={0} value={bulkBottles} onChange={(e) => setBulkBottles(parseInt(e.target.value) || 0)}
                     className="h-14 rounded-2xl text-lg" data-testid="bulk-bottles-input"/>
            </>
          )}
          <Button onClick={bulkMark} disabled={busy}
                  className={`w-full h-14 rounded-2xl font-bold ${
                    bulkStatus === "delivered" ? "bg-brand-leaf hover:bg-green-700" :
                    bulkStatus === "failed" ? "bg-brand-clay hover:bg-red-700" :
                    bulkStatus === "out_for_delivery" ? "bg-brand-sky hover:bg-blue-700" :
                    "bg-brand-ink hover:bg-neutral-800"
                  } text-white`}
                  data-testid="confirm-bulk-mark">
            {busy ? "Processing…" : `Confirm — ${selected.length} deliveries`}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Pack Summary dialog (admin) */}
      <Dialog open={packOpen} onOpenChange={(o) => { if (!o) { setPackOpen(false); setPackData(null); } }}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="admin-pack-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Pack Summary</DialogTitle>
          </DialogHeader>
          {packBusy && <div className="text-neutral-500 text-sm py-6 text-center">Calculating…</div>}
          {!packBusy && packData && (
            <>
              <div className="flex gap-2 flex-wrap" data-testid="admin-pack-slot-chips">
                {[
                  { v: null, label: "All slots" },
                  { v: "morning", label: "Morning" },
                  { v: "evening", label: "Evening" },
                ].map((s) => (
                  <button key={s.label} onClick={() => reloadPack(s.v)}
                          data-testid={`admin-pack-slot-${s.v || "all"}`}
                          className={`h-9 px-3 rounded-full text-xs font-bold border ${
                            (packData.slot === "all" ? null : packData.slot) === s.v
                              ? "bg-brand-ink text-white border-brand-ink"
                              : "bg-white text-neutral-700 border-neutral-200"
                          }`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-3 text-xs font-bold text-neutral-600 cursor-pointer"
                     data-testid="admin-pack-include-completed">
                <input type="checkbox" checked={packIncludeCompleted} onChange={togglePackIncludeCompleted}
                       className="w-4 h-4 rounded accent-brand-sky"/>
                Show full day total (include delivered / failed / skipped)
              </label>
              <div className="bg-brand-cream rounded-2xl p-3 mt-3 text-sm grid grid-cols-3 gap-2" data-testid="admin-pack-totals">
                <div><div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Deliveries</div><div className="font-heading text-xl font-extrabold">{packData.total_deliveries}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Customers</div><div className="font-heading text-xl font-extrabold">{packData.total_customers}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Amount</div><div className="font-heading text-xl font-extrabold">₹{packData.total_amount}</div></div>
              </div>
              {packData.totals_by_unit && packData.totals_by_unit.length > 0 && (
                <div className="bg-white border border-neutral-200 rounded-2xl p-3 mt-2 flex flex-wrap gap-2"
                     data-testid="admin-pack-totals-by-unit">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold w-full">Total volume to load</div>
                  {packData.totals_by_unit.map((t) => (
                    <span key={t.unit}
                          data-testid={`admin-pack-total-unit-${t.unit}`}
                          className="px-3 py-1 rounded-full bg-brand-ink text-white text-xs font-bold">
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
              {packData.by_route && packData.by_route.length > 1 && (
                <div className="bg-white border border-neutral-200 rounded-2xl p-3 mt-2"
                     data-testid="admin-pack-by-route">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-2">Route-wise breakdown</div>
                  <ul className="space-y-1.5">
                    {packData.by_route.map((rt) => (
                      <li key={rt.route_id || "unassigned"}
                          data-testid={`admin-pack-route-${rt.route_id || "unassigned"}`}
                          className="flex items-center justify-between gap-3 text-sm">
                        <span className="font-bold truncate">{rt.route_name}</span>
                        <span className="flex flex-wrap gap-1 justify-end">
                          {rt.by_unit_display.map((t) => (
                            <span key={t.unit} className="px-2 py-0.5 rounded-full bg-brand-cream text-neutral-800 text-[11px] font-bold">
                              {t.label}
                            </span>
                          ))}
                          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold self-center">· {rt.delivery_count} drops</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {packData.items.length === 0 ? (
                <EmptyState icon={Package} size="sm" title="Nothing to pack" description="No items match this filter." testId="admin-pack-empty" />
              ) : (
                <ul className="space-y-2 mt-3 max-h-[55vh] overflow-y-auto" data-testid="admin-pack-list">
                  {packData.items.map((it) => (
                    <li key={packItemKey(it)} className="bg-white border border-neutral-200 rounded-2xl p-3 flex items-center gap-3"
                        data-testid={`admin-pack-item-${packItemKey(it)}`}>
                      <div className="w-12 h-12 rounded-xl bg-brand-cream overflow-hidden shrink-0">
                        {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover"/> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{it.product_name}</div>
                        <div className="text-[11px] text-neutral-500">{it.pack_label || it.pack_size}{(it.pack_label || it.pack_size) ? " · " : ""}{it.customer_count} {it.customer_count === 1 ? "customer" : "customers"} · {it.delivery_count} drops</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading text-2xl font-extrabold leading-none">{it.total_quantity}</div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">to pack</div>
                        {it.volume_label && it.volume_unit !== "pack" && (
                          <div className="text-[11px] mt-0.5 font-bold text-brand-ink"
                               data-testid={`admin-pack-item-volume-${packItemKey(it)}`}>
                            = {it.volume_label}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
export function AdminRoutes() {
  const { user } = useAuth();
  const isTenantAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [routes, setRoutes] = useState([]);
  const [riders, setRiders] = useState([]);
  const [tenantSlots, setTenantSlots] = useState([]); // [{label:"Morning", slot_start, slot_end, is_active}, ...]
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [openR, setOpenR] = useState(false);
  const [openB, setOpenB] = useState(false);
  const [rForm, setRForm] = useState({ name: "", area: "", pincodes: "", allowed_slots: [] });
  const [bForm, setBForm] = useState({ name: "", phone: "" });
  // Edit rider
  const [editRider, setEditRider] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "" });
  // Edit route
  const [editRoute, setEditRoute] = useState(null);
  const [editRouteForm, setEditRouteForm] = useState({ name: "", area: "", pincodes: "", allowed_slots: [] });
  // Supervisors (tenant_admin only — restricted-access staff accounts)
  const [supervisors, setSupervisors] = useState([]);
  const [includeInactiveSup, setIncludeInactiveSup] = useState(false);
  const [openS, setOpenS] = useState(false);
  const [sForm, setSForm] = useState({ name: "", phone: "" });
  const [editSup, setEditSup] = useState(null);
  const [editSupForm, setEditSupForm] = useState({ name: "", phone: "" });

  // Apartments attached to each route (set on the Apartments page). Shown
  // on the route card so an admin can see the whole chain — apartment →
  // route → rider per slot — from here, instead of hunting for a per-rider
  // apartment setting that does not exist by design (rider_assign.py).
  const [apartmentsByRoute, setApartmentsByRoute] = useState({});
  const loadApartmentsByRoute = async () => {
    try {
      const all = [];
      let page = 1, totalPages = 1;
      do {
        const { data } = await api.get(`/api/admin/apartments?page=${page}&page_size=100&include_inactive=false`);
        all.push(...(data.apartments || []));
        totalPages = data.total_pages || 1;
        page += 1;
      } while (page <= totalPages && page <= 20);
      const grouped = {};
      all.forEach((a) => { if (a.route_id) (grouped[a.route_id] = grouped[a.route_id] || []).push(a); });
      setApartmentsByRoute(grouped);
    } catch {
      setApartmentsByRoute({});            // informational only — never blocks the page
    }
  };

  const load = async () => {
    const calls = [
      api.get("/api/admin/routes"),
      api.get(`/api/admin/delivery-boys?include_inactive=${includeInactive}`),
      // Operations-scoped (not /admin/tenant/me — that's the full tenant
      // doc incl. branding/billing/keys, tenant_admin-only) so supervisors
      // can load this page too.
      api.get("/api/admin/delivery-windows"),
    ];
    if (isTenantAdmin) calls.push(api.get(`/api/admin/supervisors?include_inactive=${includeInactiveSup}`));
    setLoadError(null);
    loadApartmentsByRoute();
    try {
      const [r, b, w, s] = await Promise.all(calls);
      setRoutes(r.data.routes);
      setRiders(b.data.delivery_boys);
      setTenantSlots((w.data.delivery_windows || []).filter((x) => x.is_active !== false));
      if (s) setSupervisors(s.data.supervisors);
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive, includeInactiveSup]);

  const createSupervisor = async () => {
    if (!sForm.name || !sForm.phone) { toast.error("Name and phone required"); return; }
    try {
      await api.post("/api/admin/supervisors", sForm);
      toast.success("Supervisor added"); setOpenS(false); load();
      setSForm({ name: "", phone: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Add supervisor failed"); }
  };
  const openEditSup = (s) => { setEditSup(s); setEditSupForm({ name: s.name || "", phone: s.phone || "" }); };
  const saveEditSup = async () => {
    if (!editSupForm.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.patch(`/api/admin/supervisors/${editSup.id}`, { name: editSupForm.name, phone: editSupForm.phone });
      toast.success("Supervisor updated"); setEditSup(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const toggleSupActive = async (s) => {
    const nextActive = !(s.is_active !== false);
    try {
      await api.patch(`/api/admin/supervisors/${s.id}`, { active: nextActive });
      toast.success(nextActive ? "Supervisor reactivated" : "Supervisor suspended");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const deleteSup = async (s) => {
    if (!window.confirm(`Remove supervisor ${s.name}?`)) return;
    try {
      await api.delete(`/api/admin/supervisors/${s.id}`);
      toast.success("Supervisor removed"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const createRoute = async () => {
    if (!rForm.name.trim()) { toast.error("Route name required"); return; }
    try {
      await api.post("/api/admin/routes", {
        name: rForm.name, area: rForm.area,
        pincodes: rForm.pincodes.split(",").map(s => s.trim()).filter(Boolean),
        allowed_slots: rForm.allowed_slots,
      });
      toast.success("Route created"); setOpenR(false); load();
      setRForm({ name: "", area: "", pincodes: "", allowed_slots: [] });
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const openEditRoute = (r) => {
    setEditRoute(r);
    setEditRouteForm({
      name: r.name || "", area: r.area || "",
      pincodes: (r.pincodes || []).join(", "),
      allowed_slots: r.allowed_slots || [],
    });
  };
  const saveRoute = async () => {
    if (!editRouteForm.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.patch(`/api/admin/routes/${editRoute.id}`, {
        name: editRouteForm.name,
        area: editRouteForm.area,
        pincodes: editRouteForm.pincodes.split(",").map((s) => s.trim()).filter(Boolean),
        allowed_slots: editRouteForm.allowed_slots,
      });
      toast.success("Route updated");
      setEditRoute(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const toggleAllowedSlot = (formSetter, form, slotLabel) => {
    const key = slotLabel.toLowerCase();
    const has = form.allowed_slots.includes(key);
    formSetter({
      ...form,
      allowed_slots: has ? form.allowed_slots.filter((s) => s !== key) : [...form.allowed_slots, key],
    });
  };
  const deleteRoute = async (r) => {
    if (!window.confirm(`Delete route "${r.name}"? Customers on this route will be unassigned. Delivery history is preserved.`)) return;
    try {
      await api.delete(`/api/admin/routes/${r.id}`);
      toast.success("Route removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const createBoy = async () => {
    if (!bForm.name || !bForm.phone) { toast.error("Name and phone required"); return; }
    try {
      await api.post("/api/admin/delivery-boys", bForm);
      toast.success("Rider added"); setOpenB(false); load();
      setBForm({ name: "", phone: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Add rider failed"); }
  };

  const assignRider = async (routeId, slot, riderId) => {
    if (!riderId) {
      // Unassign on empty selection
      try {
        await api.delete(`/api/admin/routes/${routeId}/assign-rider?slot=${encodeURIComponent(slot)}`);
        toast.success("Unassigned");
        load();
      } catch (e) { toast.error(e.response?.data?.detail || "Unassign failed"); }
      return;
    }
    try {
      const { data } = await api.put(`/api/admin/routes/${routeId}/assign-rider`, { rider_id: riderId, slot });
      toast.success(`Assigned to ${data.rider_name}${data.backfilled ? ` · ${data.backfilled} pending delivery${data.backfilled === 1 ? "" : "ies"} updated` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const assignAllSlots = async (routeId, riderId) => {
    if (!riderId) return;
    try {
      const { data } = await api.put(`/api/admin/routes/${routeId}/assign-rider`, { rider_id: riderId, slot: "all" });
      toast.success(`Assigned to ${data.rider_name} on all slots${data.backfilled ? ` · ${data.backfilled} updated` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const openEdit = (r) => {
    setEditRider(r);
    setEditForm({ name: r.name || "", phone: r.phone || "" });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.patch(`/api/admin/delivery-boys/${editRider.id}`, {
        name: editForm.name, phone: editForm.phone,
      });
      toast.success("Rider updated");
      setEditRider(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const toggleActive = async (r) => {
    const nextActive = !(r.is_active !== false);
    try {
      await api.patch(`/api/admin/delivery-boys/${r.id}`, { active: nextActive });
      toast.success(nextActive ? "Rider reactivated" : "Rider suspended");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const deleteRider = async (r) => {
    if (!window.confirm(`Remove rider ${r.name}? They'll be unassigned from routes but delivery history is preserved.`)) return;
    try {
      await api.delete(`/api/admin/delivery-boys/${r.id}`);
      toast.success("Rider removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // Filter to only active riders for the assign-route dropdown
  const activeRiders = riders.filter((r) => r.is_active !== false);

  return (
    <div className="space-y-6 page-enter" data-testid="admin-routes-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Operations</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Routes & Riders</h1>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl" data-testid="routes-assignment-flow">
            Delivery flows <span className="font-semibold">Apartment → Route → Rider (per slot)</span>. Attach an
            apartment to a route on the <Link to="/admin/apartments" className="text-brand-sky hover:underline font-semibold">Apartments</Link> page;
            assign the rider for each slot on the route here. Riders are never assigned to apartments or customers directly —
            every order at an apartment goes to its route's rider for that slot.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setOpenB(true)} variant="outline" className="h-11 rounded-2xl border-2 border-brand-ink font-bold" data-testid="add-rider-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Rider
          </Button>
          <Button onClick={() => setOpenR(true)} className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="add-route-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Route
          </Button>
        </div>
      </div>

      {loading && <LoadingState />}

      {!loading && loadError && (
        <EmptyState icon={AlertTriangle} title="Couldn't load routes & riders" description={loadError}
          action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>} />
      )}

      {!loading && !loadError && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-heading font-bold mb-3">Routes ({routes.filter((r) => r.is_active !== false).length})</h3>
          <div className="space-y-3">
            {routes.filter((r) => r.is_active !== false).map((r) => {
              const slotKey = (s) => (s || "").toLowerCase();
              const resolved = r.riders_by_slot_resolved || {};
              const assignedSet = new Set(Object.values(r.riders_by_slot || {}));
              return (
                <div key={r.id} className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid={`route-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold">{r.name}</div>
                      <div className="text-sm text-neutral-600">{r.area || "—"}</div>
                      <div className="text-xs text-neutral-500 mt-1">Pincodes: {(r.pincodes || []).join(", ") || "—"}</div>
                      <div className="text-xs text-neutral-500 mt-1" data-testid={`route-apartments-${r.id}`}>
                        Apartments: {(apartmentsByRoute[r.id] || []).length
                          ? (apartmentsByRoute[r.id] || []).map((a) => a.name).join(", ")
                          : "none attached"}
                        {" "}<Link to="/admin/apartments" className="text-brand-sky hover:underline font-semibold">(attach on Apartments)</Link>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEditRoute(r)} className="h-9 px-3 rounded-full bg-brand-sky/10 text-brand-sky text-xs font-bold inline-flex items-center gap-1" data-testid={`edit-route-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5"/> Edit
                      </button>
                      <button onClick={() => deleteRoute(r)} className="h-9 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1" data-testid={`delete-route-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  </div>

                  {/* Per-slot rider assignment matrix */}
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Assign rider per slot</label>
                      {tenantSlots.length > 1 && (
                        <select onChange={(e) => { if (e.target.value) { assignAllSlots(r.id, e.target.value); e.target.value = ""; } }}
                                defaultValue=""
                                className="h-8 px-2 rounded-full text-[11px] font-bold border border-neutral-200 bg-white text-neutral-700"
                                data-testid={`assign-all-${r.id}`}>
                          <option value="">⚡ Assign to all slots</option>
                          {activeRiders.map((rd) => <option key={rd.id} value={rd.id}>{rd.name}</option>)}
                        </select>
                      )}
                    </div>
                    {tenantSlots.length === 0 ? (
                      <div className="text-xs text-neutral-500 rounded-2xl border border-dashed border-neutral-200 px-3 py-2" data-testid={`no-slots-${r.id}`}>
                        No active delivery slots — enable a slot under <b>Settings → Delivery Windows</b> first.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {tenantSlots.map((w) => {
                          const key = slotKey(w.label);
                          const current = (r.riders_by_slot || {})[key] || "";
                          const info = resolved[key];
                          return (
                            <div key={key} className="flex items-center gap-2" data-testid={`slot-row-${r.id}-${key}`}>
                              <div className="w-24 shrink-0 text-xs font-bold uppercase tracking-wider text-neutral-600">{w.label}</div>
                              <select value={current} onChange={(e) => assignRider(r.id, key, e.target.value)}
                                      className="flex-1 h-10 px-3 rounded-2xl border border-neutral-200 bg-white text-sm"
                                      data-testid={`assign-rider-${r.id}-${key}`}>
                                <option value="">— pick a rider —</option>
                                {activeRiders.map((rd) => <option key={rd.id} value={rd.id}>{rd.name}</option>)}
                              </select>
                              {info && (
                                <span className="hidden sm:inline-flex items-center gap-1 px-2 h-7 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold" data-testid={`rider-chip-${r.id}-${key}`}>
                                  <Truck className="w-3 h-3"/> {info.name}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {assignedSet.size > 0 && (
                      <div className="text-[10px] text-neutral-500 mt-2">{assignedSet.size} rider{assignedSet.size === 1 ? "" : "s"} assigned across {Object.keys(r.riders_by_slot || {}).length} slot{Object.keys(r.riders_by_slot || {}).length === 1 ? "" : "s"}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold">Riders ({riders.length})</h3>
            <label className="text-xs font-bold text-neutral-600 inline-flex items-center gap-1.5 cursor-pointer" data-testid="toggle-include-inactive">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="rounded"/>
              Show inactive
            </label>
          </div>
          <div className="space-y-3">
            {riders.map((r) => {
              const inactive = r.is_active === false;
              return (
                <div key={r.id} className={`bg-white rounded-3xl border p-5 ${inactive ? "border-neutral-200 opacity-60" : "border-neutral-200"}`} data-testid={`rider-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-heading font-extrabold ${inactive ? "bg-neutral-200 text-neutral-500" : "bg-brand-clay/10 text-brand-clay"}`}>
                      {r.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center gap-2">
                        {r.name}
                        {(inactive || r.deleted_at) && <span className="px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600 text-[10px] font-extrabold uppercase tracking-wider" data-testid={`rider-inactive-badge-${r.id}`}>{r.deleted_at ? "Removed" : "Suspended"}</span>}
                      </div>
                      <div className="text-sm text-neutral-500 font-mono">{r.phone}</div>
                    </div>
                    {r.current_lat && !inactive && <span className="text-xs text-brand-leaf font-bold inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-leaf animate-pulse" /> Online</span>}
                  </div>
                  <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap gap-2">
                    <button onClick={() => openEdit(r)}
                            className="h-9 px-3 rounded-full bg-brand-sky/10 text-brand-sky text-xs font-bold inline-flex items-center gap-1"
                            data-testid={`edit-rider-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5"/> Edit
                    </button>
                    <button onClick={() => toggleActive(r)}
                            className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 ${inactive ? "bg-brand-leaf/10 text-brand-leaf" : "bg-brand-sand/10 text-brand-sand"}`}
                            data-testid={`toggle-rider-${r.id}`}>
                      <Power className="w-3.5 h-3.5"/> {inactive ? "Reactivate" : "Suspend"}
                    </button>
                    {!inactive && (
                      <button onClick={() => deleteRider(r)}
                              className="h-9 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1"
                              data-testid={`delete-rider-${r.id}`}>
                        <UserX className="w-3.5 h-3.5"/> Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {riders.length === 0 && (
              <div className="bg-white border border-dashed border-neutral-200 rounded-3xl" data-testid="no-riders">
                <EmptyState icon={Users} title="No riders yet" description={<>Click &quot;Add Rider&quot; to onboard one.</>} />
              </div>
            )}
          </div>
        </div>
      </div>}

      {isTenantAdmin && !loading && !loadError && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Restricted staff access</div>
              <h3 className="font-heading font-bold">Supervisors ({supervisors.length})</h3>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-neutral-600 inline-flex items-center gap-1.5 cursor-pointer" data-testid="toggle-include-inactive-sup">
                <input type="checkbox" checked={includeInactiveSup} onChange={(e) => setIncludeInactiveSup(e.target.checked)} className="rounded"/>
                Show inactive
              </label>
              <Button onClick={() => setOpenS(true)} variant="outline" className="h-10 rounded-2xl border-2 border-brand-ink font-bold" data-testid="add-supervisor-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Supervisor
              </Button>
            </div>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Supervisors log in at the same admin login and can monitor orders/deliveries, assign riders,
            and view reports — but can&apos;t touch Settings, Billing, Wallet, Catalog, or Branding.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {supervisors.map((s) => {
              const inactive = s.is_active === false;
              return (
                <div key={s.id} className={`bg-white rounded-3xl border p-5 ${inactive ? "border-neutral-200 opacity-60" : "border-neutral-200"}`} data-testid={`supervisor-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-heading font-extrabold ${inactive ? "bg-neutral-200 text-neutral-500" : "bg-brand-sky/10 text-brand-sky"}`}>
                      {s.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center gap-2">
                        {s.name}
                        {inactive && <span className="px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600 text-[10px] font-extrabold uppercase tracking-wider">Inactive</span>}
                      </div>
                      <div className="text-sm text-neutral-500 font-mono">{s.phone}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap gap-2">
                    <button onClick={() => openEditSup(s)}
                            className="h-9 px-3 rounded-full bg-brand-sky/10 text-brand-sky text-xs font-bold inline-flex items-center gap-1"
                            data-testid={`edit-supervisor-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5"/> Edit
                    </button>
                    <button onClick={() => toggleSupActive(s)}
                            className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 ${inactive ? "bg-brand-leaf/10 text-brand-leaf" : "bg-brand-sand/10 text-brand-sand"}`}
                            data-testid={`toggle-supervisor-${s.id}`}>
                      <Power className="w-3.5 h-3.5"/> {inactive ? "Reactivate" : "Suspend"}
                    </button>
                    {!inactive && (
                      <button onClick={() => deleteSup(s)}
                              className="h-9 px-3 rounded-full bg-brand-clay/10 text-brand-clay text-xs font-bold inline-flex items-center gap-1"
                              data-testid={`delete-supervisor-${s.id}`}>
                        <UserX className="w-3.5 h-3.5"/> Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {supervisors.length === 0 && (
              <div className="bg-white border border-dashed border-neutral-200 rounded-3xl sm:col-span-2" data-testid="no-supervisors">
                <EmptyState icon={ShieldCheck} title="No supervisors yet" description={<>Click &quot;Add Supervisor&quot; to onboard one.</>} />
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={openS} onOpenChange={setOpenS}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New Supervisor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="supervisor-name" />
            <Input placeholder="Phone * (10-digit or +91…)" value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} className="h-12 rounded-2xl" data-testid="supervisor-phone" />
            <Button onClick={createSupervisor} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-supervisor-btn">Add Supervisor</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSup} onOpenChange={(o) => !o && setEditSup(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="edit-supervisor-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit Supervisor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={editSupForm.name} onChange={(e) => setEditSupForm({ ...editSupForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-supervisor-name" />
            <Input placeholder="Phone" value={editSupForm.phone} onChange={(e) => setEditSupForm({ ...editSupForm, phone: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-supervisor-phone" />
            <Button onClick={saveEditSup} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-edit-supervisor-btn">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openR} onOpenChange={setOpenR}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New Route</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Route name *" value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="route-name" />
            <Input placeholder="Area" value={rForm.area} onChange={(e) => setRForm({ ...rForm, area: e.target.value })} className="h-12 rounded-2xl" data-testid="route-area" />
            <Input placeholder="Pincodes (comma-separated)" value={rForm.pincodes} onChange={(e) => setRForm({ ...rForm, pincodes: e.target.value })} className="h-12 rounded-2xl" data-testid="route-pincodes" />
            <div>
              <div className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">
                Delivery slots allowed for this area (blank = all)
              </div>
              <div className="flex flex-wrap gap-2">
                {tenantSlots.map((w) => (
                  <button key={w.label} type="button"
                          onClick={() => toggleAllowedSlot(setRForm, rForm, w.label)}
                          className={`h-9 px-3 rounded-full text-xs font-bold border ${
                            rForm.allowed_slots.includes(w.label.toLowerCase())
                              ? "bg-brand-sky text-white border-brand-sky"
                              : "bg-white text-neutral-700 border-neutral-200"
                          }`}
                          data-testid={`route-allowed-slot-${w.label.toLowerCase()}`}>
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-2xl bg-brand-sand/10 border border-brand-sand/30 px-3 py-2 text-[11px] text-brand-ink/80">
              Routes are slot-agnostic by default. Assign a rider per slot (Morning, Evening, Afternoon…) after creating the route — based on the slots you have active under Settings → Delivery Windows.
            </div>
            <Button onClick={createRoute} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-route-btn">Create Route</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRoute} onOpenChange={(o) => !o && setEditRoute(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="edit-route-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit Route</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Route name *" value={editRouteForm.name} onChange={(e) => setEditRouteForm({ ...editRouteForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-route-name" />
            <Input placeholder="Area" value={editRouteForm.area} onChange={(e) => setEditRouteForm({ ...editRouteForm, area: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-route-area" />
            <Input placeholder="Pincodes (comma-separated)" value={editRouteForm.pincodes} onChange={(e) => setEditRouteForm({ ...editRouteForm, pincodes: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-route-pincodes" />
            <div>
              <div className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">
                Delivery slots allowed for this area (blank = all)
              </div>
              <div className="flex flex-wrap gap-2">
                {tenantSlots.map((w) => (
                  <button key={w.label} type="button"
                          onClick={() => toggleAllowedSlot(setEditRouteForm, editRouteForm, w.label)}
                          className={`h-9 px-3 rounded-full text-xs font-bold border ${
                            editRouteForm.allowed_slots.includes(w.label.toLowerCase())
                              ? "bg-brand-sky text-white border-brand-sky"
                              : "bg-white text-neutral-700 border-neutral-200"
                          }`}
                          data-testid={`edit-route-allowed-slot-${w.label.toLowerCase()}`}>
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={saveRoute} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-edit-route-btn">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openB} onOpenChange={setOpenB}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New Rider</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="rider-name" />
            <Input placeholder="Phone * (10-digit or +91…)" value={bForm.phone} onChange={(e) => setBForm({ ...bForm, phone: e.target.value })} className="h-12 rounded-2xl" data-testid="rider-phone" />
            <Button onClick={createBoy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-rider-btn">Add Rider</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRider} onOpenChange={(o) => !o && setEditRider(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="edit-rider-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit Rider</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-rider-name" />
            <Input placeholder="Phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="h-12 rounded-2xl" data-testid="edit-rider-phone" />
            <Button onClick={saveEdit} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-edit-rider-btn">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ BOTTLES ============
export function AdminBottles() {
  const [view, setView] = useState("counter"); // counter | inventory
  const [data, setData] = useState({ bottles: [], stats: {} });
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjCustomer, setAdjCustomer] = useState(null);
  const [adjDelta, setAdjDelta] = useState(0);
  const [adjNote, setAdjNote] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoadError(null);
    try {
      if (view === "inventory") {
        const r = await api.get("/api/admin/bottles");
        setData(r.data);
      } else {
        const r = await api.get("/api/admin/bottles/counters");
        setCounters(r.data);
      }
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [view]);

  const add = async () => {
    if (!code) return;
    await api.post("/api/admin/bottles", { code });
    toast.success("Bottle added");
    setOpen(false); setCode(""); load();
  };

  const openAdjust = (c, delta) => {
    setAdjCustomer(c); setAdjDelta(delta); setAdjNote(""); setAdjOpen(true);
  };

  const submitAdjust = async () => {
    if (!adjCustomer || adjDelta === 0) return;
    await api.post(`/api/admin/customers/${adjCustomer.id}/bottles/adjust`, { delta: adjDelta, note: adjNote });
    toast.success(`Adjusted ${adjDelta > 0 ? "+" : ""}${adjDelta} for ${adjCustomer.name}`);
    setAdjOpen(false); load();
  };

  const filteredCustomers = (counters?.customers || []).filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search)
  );

  return (
    <div className="space-y-6 page-enter" data-testid="admin-bottles-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Inventory</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Bottle Tracking</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto" data-testid="add-bottle-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Bottle (by ID)
        </Button>
      </div>

      {/* View toggle */}
      <div className="inline-flex p-1 rounded-2xl bg-white border border-neutral-200" data-testid="view-toggle">
        <button onClick={() => setView("counter")}
                className={`px-5 h-10 rounded-xl text-sm font-bold transition-colors ${view === "counter" ? "bg-brand-ink text-white" : "text-neutral-600"}`}
                data-testid="view-counter">📊 Counter view</button>
        <button onClick={() => setView("inventory")}
                className={`px-5 h-10 rounded-xl text-sm font-bold transition-colors ${view === "inventory" ? "bg-brand-ink text-white" : "text-neutral-600"}`}
                data-testid="view-inventory">🔖 By ID (tracked)</button>
      </div>

      {loadError && (
        <EmptyState icon={Box} title="Couldn't load bottle tracking" description={loadError}
          action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>} />
      )}

      {!loadError && loading && <LoadingState />}

      {!loading && view === "counter" && counters && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <CounterCard label="With customers" value={counters.with_customers_total} color="text-brand-sky" testid="counter-with-customers" />
            <CounterCard label="In inventory" value={counters.inventory.in_inventory} color="text-brand-leaf" testid="counter-inventory" />
            <CounterCard label="Tracked (by ID)" value={counters.inventory.total_tracked} color="text-brand-ink" testid="counter-tracked" />
            <CounterCard label="Damaged + Lost" value={counters.inventory.damaged + counters.inventory.lost} color="text-brand-clay" testid="counter-damaged-lost" />
          </div>

          <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-neutral-100">
              <h3 className="font-heading font-bold">Per-customer counters</h3>
              <div className="relative">
                <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
                       className="h-10 rounded-2xl w-44 sm:w-60" data-testid="counter-search"/>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
                  <tr>
                    <th className="text-left px-5 py-3">Customer</th>
                    <th className="text-left px-5 py-3">Phone</th>
                    <th className="text-center px-5 py-3">Bottles held</th>
                    <th className="text-right px-5 py-3">Adjust</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {filteredCustomers.map((c) => (
                    <tr key={c.id} className="hover:bg-neutral-50" data-testid={`counter-row-${c.id}`}>
                      <td className="px-5 py-3 font-semibold">{c.name}</td>
                      <td className="px-5 py-3 font-mono text-sm">{c.phone}</td>
                      <td className="px-5 py-3 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[40px] h-8 rounded-2xl font-heading font-extrabold text-lg ${c.bottles_held > 0 ? "bg-brand-sky/10 text-brand-sky" : "bg-neutral-100 text-neutral-500"}`}>
                          🍶 {c.bottles_held}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openAdjust(c, -1)} className="w-8 h-8 rounded-full bg-brand-clay/10 text-brand-clay font-bold hover:bg-brand-clay hover:text-white transition-colors" data-testid={`minus-${c.id}`}>−</button>
                          <button onClick={() => openAdjust(c, 1)} className="w-8 h-8 rounded-full bg-brand-leaf/10 text-brand-leaf font-bold hover:bg-brand-leaf hover:text-white transition-colors" data-testid={`plus-${c.id}`}>+</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredCustomers.length === 0 && <tr><td colSpan="4"><EmptyState icon={Users} size="sm" title="No customers" /></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && view === "inventory" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              ["total", "Total", "text-brand-ink"],
              ["in_inventory", "Inventory", "text-brand-leaf"],
              ["assigned", "Assigned", "text-brand-sky"],
              ["in_use", "In Use", "text-brand-sand"],
              ["damaged", "Damaged", "text-brand-clay"],
              ["lost", "Lost", "text-brand-clay"],
            ].map(([k, l, c]) => (
              <div key={k} className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid={`bottle-stat-${k}`}>
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">{l}</div>
                <div className={`font-heading text-3xl font-extrabold mt-2 ${c}`}>{data.stats[k] || 0}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-5 py-3">Code</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Assigned</th>
                  <th className="text-left px-5 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.bottles.slice(0, 30).map((b) => (
                  <tr key={b.id} data-testid={`bottle-row-${b.id}`} className="hover:bg-neutral-50">
                    <td className="px-5 py-3 font-mono font-bold">{b.code}</td>
                    <td className="px-5 py-3"><span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase bg-neutral-100">{b.status.replace("_", " ")}</span></td>
                    <td className="px-5 py-3 text-sm text-neutral-500">{b.assigned_customer_id ? b.assigned_customer_id.slice(0, 8) : "—"}</td>
                    <td className="px-5 py-3 text-sm text-neutral-500">{formatDate(b.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}

      {/* Add bottle dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New Bottle (Tracked)</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-600">Add a bottle with a unique code/QR for individual lifecycle tracking.</p>
          <Input placeholder="Bottle code / QR" value={code} onChange={(e) => setCode(e.target.value)} className="h-12 rounded-2xl font-mono" data-testid="bottle-code-input"/>
          <Button onClick={add} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="confirm-add-bottle">Add</Button>
        </DialogContent>
      </Dialog>

      {/* Counter adjust dialog */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Adjust counter</DialogTitle></DialogHeader>
          {adjCustomer && (
            <>
              <div className="bg-brand-cream rounded-2xl p-4">
                <div className="font-bold">{adjCustomer.name}</div>
                <div className="text-sm text-neutral-600">Currently holds: 🍶 {adjCustomer.bottles_held}</div>
              </div>
              <div className="flex items-center justify-between gap-3 mt-2">
                <span className="text-sm text-neutral-600">Change by</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setAdjDelta(adjDelta - 1)} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200">−</button>
                  <span className="font-mono font-extrabold text-xl w-12 text-center" data-testid="adj-delta-value">{adjDelta > 0 ? `+${adjDelta}` : adjDelta}</span>
                  <button onClick={() => setAdjDelta(adjDelta + 1)} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200">+</button>
                </div>
              </div>
              <Input placeholder="Note (optional)" value={adjNote} onChange={(e) => setAdjNote(e.target.value)} className="h-12 rounded-2xl" data-testid="adj-note"/>
              <Button onClick={submitAdjust} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="confirm-adjust">
                Apply ({adjDelta > 0 ? "+" : ""}{adjDelta})
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CounterCard({ label, value, color, testid }) {
  return (
    <div className="bg-white rounded-3xl border border-neutral-200 p-4 sm:p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className={`font-heading text-3xl sm:text-4xl font-extrabold mt-2 ${color}`}>🍶 {value}</div>
    </div>
  );
}

// ============ REELS ============
export function AdminReels() {
  const [reels, setReels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", video_url: "", thumbnail_url: "" });

  const load = () => api.get("/api/admin/reels").then(({ data }) => setReels(data.reels)).catch((e) => toast.error(friendlyMessage(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.video_url) { toast.error("Title and video URL required"); return; }
    await api.post("/api/admin/reels", form);
    toast.success("Reel published"); setOpen(false);
    setForm({ title: "", description: "", video_url: "", thumbnail_url: "" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/api/admin/reels/${id}`);
    load();
  };

  return (
    <div className="space-y-6 page-enter" data-testid="admin-reels-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Engagement</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Reels</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto" data-testid="add-reel-btn">
          <Plus className="w-4 h-4 mr-1" /> Upload Reel
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {reels.map((r) => (
          <div key={r.id} className={`bg-white rounded-3xl border border-neutral-200 overflow-hidden ${!r.is_active && "opacity-50"}`} data-testid={`reel-${r.id}`}>
            <div className="aspect-video bg-neutral-100 relative">
              <img src={r.thumbnail_url} alt={r.title} className="w-full h-full object-cover" />
              <Film className="absolute top-3 right-3 w-5 h-5 text-white drop-shadow-lg" />
            </div>
            <div className="p-4">
              <div className="font-heading font-bold">{r.title}</div>
              <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{r.description}</div>
              <div className="flex items-center justify-between mt-3 text-sm">
                <span className="text-neutral-500">👁 {r.views} · ❤ {r.likes}</span>
                <button onClick={() => del(r.id)} className="text-brand-clay font-bold text-sm inline-flex items-center gap-1" data-testid={`delete-reel-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Upload Reel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-12 rounded-2xl" data-testid="reel-title" />
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-12 rounded-2xl" data-testid="reel-desc" />
            <Input placeholder="Video URL (mp4) *" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="h-12 rounded-2xl" data-testid="reel-video" />
            <Input placeholder="Thumbnail URL" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="h-12 rounded-2xl" data-testid="reel-thumb" />
            <Button onClick={save} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-reel-btn">Publish</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ REPORTS ============
function downloadFromApi(url, filename) {
  const token = getStoredToken();
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) {
        const body = await r.text();
        throw new Error(body || "Download failed");
      }
      const blob = await r.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success("Downloaded");
    })
    .catch((e) => toast.error(e?.response?.data?.detail || "Download failed"));
}

function downloadExcel(date, group_by, status) {
  const url = `${BACKEND_URL}/api/admin/reports/delivery-aggregate.xlsx?date=${date}&group_by=${group_by}${status ? `&status=${status}` : ""}`;
  downloadFromApi(url, `deliveries_${date}_by_${group_by}.xlsx`);
}

function downloadSlotReport(date, slot) {
  const url = `${BACKEND_URL}/api/admin/reports/orders-slot.xlsx?slot=${slot}&date=${date}`;
  downloadFromApi(url, `orders_${slot}_${date}.xlsx`);
}

function downloadRiderReport(date, riderId) {
  const url = `${BACKEND_URL}/api/admin/reports/rider-daily.xlsx?date=${date}${riderId ? `&rider_id=${riderId}` : ""}`;
  downloadFromApi(url, `rider_daily_${date}${riderId ? `_${riderId}` : ""}.xlsx`);
}

const billFileName = (customerName, start, end) =>
  `invoice_${(customerName || "customer").replace(/\s+/g, "_")}_${start}_${end}.pdf`;

// Bill PDFs go through lib/billDownload so they also open inside the mobile
// app (a blob download does nothing in its WebView).
function billPdf(request) {
  return downloadBillPdf({ linkPath: "/api/admin/reports/invoices/pdf-link", ...request })
    .then((how) => { if (how === "saved") toast.success("Downloaded"); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Download failed"));
}

// Generates (or refreshes) this customer's bill for the period, then downloads it.
function downloadCustomerBill(customerId, start, end, customerName) {
  return billPdf({
    pdfPath: `/api/admin/reports/customer-bill.pdf?customer_id=${customerId}&start=${start}&end=${end}`,
    linkBody: { customer_id: customerId, start, end },
    filename: billFileName(customerName, start, end),
  });
}

// Downloads one bill from the list, exactly as stored — nothing is regenerated.
function downloadInvoice(inv) {
  return billPdf({
    pdfPath: `/api/admin/reports/invoices/${inv.id}/pdf`,
    linkBody: { invoice_id: inv.id },
    filename: billFileName(inv.customer_name, inv.period_start, inv.period_end),
  });
}

export function AdminReports() {
  const { user } = useAuth();
  const isTenantAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [data, setData] = useState({ product_sales: [], daily_stats: [] });
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [sheetDate, setSheetDate] = useState(todayISO());
  const [groupBy, setGroupBy] = useState("product");
  const [statusFilter, setStatusFilter] = useState("");
  const loadOverview = () => {
    setOverviewError(null);
    return api.get("/api/admin/reports/overview").then(({ data }) => setData(data))
      .catch((e) => { setOverviewError(friendlyMessage(e)); toast.error(friendlyMessage(e)); });
  };
  useEffect(() => { loadOverview().finally(() => setOverviewLoading(false)); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-6 page-enter" data-testid="admin-reports-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Analytics</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Reports · Last 30 days</h1>
      </div>

      {/* Delivery sheet download */}
      <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="delivery-sheet-section">
        <h3 className="font-heading font-bold mb-1">Daily delivery sheet (Excel)</h3>
        <p className="text-sm text-neutral-600 mb-4">Aggregate quantity of each product / brand / category for a chosen date — perfect for the morning packing run.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Date</label>
            <Input type="date" value={sheetDate} onChange={(e) => setSheetDate(e.target.value)} className="h-11 rounded-2xl mt-1" data-testid="sheet-date"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Group by</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
                    className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1" data-testid="sheet-group-by">
              <option value="product">Product</option>
              <option value="brand">Brand</option>
              <option value="category">Category</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1" data-testid="sheet-status">
              <option value="">All</option>
              {/* Backend reads "pending" as all open work not yet out for
                  delivery: placed / confirmed / pending. */}
              <option value="pending">Pending (not yet out)</option>
              <option value="out_for_delivery">Out for delivery</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={() => downloadExcel(sheetDate, groupBy, statusFilter)}
                    className="h-11 rounded-2xl bg-brand-leaf hover:bg-green-700 font-bold w-full"
                    data-testid="download-sheet-btn">
              Download .xlsx
            </Button>
          </div>
        </div>
      </section>

      {/* Slot-based order reports (Morning/Evening) — preview + export */}
      <OrdersSlotReport sheetDate={sheetDate} setSheetDate={setSheetDate} />

      {/* Rider daily work report — preview + export */}
      <RiderDailyReport sheetDate={sheetDate} setSheetDate={setSheetDate} />

      {/* Customer bills — PDF. Billing-adjacent, tenant_admin/super_admin only —
          not shown to supervisors (matches /admin/reports/invoices staying
          require_tenant_admin on the backend). */}
      {isTenantAdmin && <CustomerBillingSection sheetDate={sheetDate} setSheetDate={setSheetDate} />}

      {overviewLoading && <LoadingState />}

      {!overviewLoading && overviewError && (
        <EmptyState icon={AlertTriangle} title="Couldn't load report data" description={overviewError}
          action={<button onClick={() => { setOverviewLoading(true); loadOverview().finally(() => setOverviewLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>} />
      )}

      {!overviewLoading && !overviewError && <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-neutral-200 p-6">
          <h3 className="font-heading font-bold mb-4">Product-wise Sales</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.product_sales} layout="vertical">
                <CartesianGrid stroke="#eee" horizontal={false}/>
                <XAxis type="number" stroke="#9ca3af" fontSize={11}/>
                <YAxis dataKey="_id" type="category" stroke="#9ca3af" fontSize={11} width={130}/>
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12 }}/>
                <Bar dataKey="revenue" fill="#3A86FF" radius={[0, 8, 8, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-neutral-200 p-6">
          <h3 className="font-heading font-bold mb-4">Daily Deliveries</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={data.daily_stats}>
                <CartesianGrid stroke="#eee" vertical={false}/>
                <XAxis dataKey="_id" tickFormatter={(s) => s.slice(5)} stroke="#9ca3af" fontSize={11}/>
                <YAxis stroke="#9ca3af" fontSize={11}/>
                <Tooltip contentStyle={{ borderRadius: 12 }}/>
                <Line type="monotone" dataKey="delivered" stroke="#81B29A" strokeWidth={3}/>
                <Line type="monotone" dataKey="failed" stroke="#E07A5F" strokeWidth={2}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
            <tr><th className="text-left px-5 py-3">Product</th><th className="text-right px-5 py-3">Deliveries</th><th className="text-right px-5 py-3">Quantity</th><th className="text-right px-5 py-3">Revenue</th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {data.product_sales.map((s) => (
              <tr key={s._id} data-testid={`report-row-${s._id}`} className="hover:bg-neutral-50">
                <td className="px-5 py-3 font-semibold">{s._id}</td>
                <td className="px-5 py-3 text-right">{s.deliveries}</td>
                <td className="px-5 py-3 text-right">{s.qty}</td>
                <td className="px-5 py-3 text-right font-mono font-bold">{formatINR(s.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      </>}
    </div>
  );
}


// ============ CUSTOMER BILLING (weekly/monthly PDF invoices) ============

// ============ ORDERS SLOT REPORT — preview table + Excel export ============
function OrdersSlotReport({ sheetDate, setSheetDate }) {
  const [slot, setSlot] = useState("morning");
  const [status, setStatus] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [data, setData] = useState({ rows: [], count: 0, total_amount: 0, total_quantity: 0, cancelled_count: 0, cancelled_quantity: 0, cancelled_amount: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Customer-name filter is a convenience, not core to this report —
    // /admin/customers is tenant_admin-only, so a supervisor viewing this
    // page simply doesn't get the filter dropdown rather than the whole
    // report crashing.
    api.get("/api/admin/customers").then(({ data }) => setCustomers(data.customers || [])).catch(() => setCustomers([]));
    // Products are operations-scoped (supervisors can read them), so this
    // filter stays available to everyone who can see the report.
    api.get("/api/admin/products").then(({ data }) => setProducts(data.products || [])).catch(() => setProducts([]));
  }, []);

  // Shared by the preview fetch and the Excel export so the downloaded
  // sheet always matches exactly what's filtered on screen.
  const buildParams = () => {
    const params = new URLSearchParams({ slot, date: sheetDate });
    if (status) params.append("status", status);
    if (customerId) params.append("customer_id", customerId);
    if (productId) params.append("product_id", productId);
    return params;
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/api/admin/reports/orders-slot?${buildParams()}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [slot, sheetDate, status, customerId, productId]);

  const exportXlsx = () => {
    const url = `${BACKEND_URL}/api/admin/reports/orders-slot.xlsx?${buildParams()}`;
    downloadFromApi(url, `orders_${slot}_${sheetDate}.xlsx`);
  };

  return (
    <section className="bg-white rounded-3xl border border-neutral-200 overflow-hidden" data-testid="slot-report-section">
      <div className="px-6 py-4 border-b border-neutral-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold">Morning / Evening order report</h3>
          <p className="text-xs text-neutral-500">Preview orders on-screen, then export the filtered view.</p>
        </div>
        <Button onClick={exportXlsx} disabled={busy || data.rows.length === 0}
                className="h-10 rounded-2xl bg-brand-leaf hover:bg-green-700 font-bold px-6"
                data-testid="export-slot-xlsx">
          Export to Excel
        </Button>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 border-b border-neutral-100 bg-neutral-50">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Date</label>
          <Input type="date" value={sheetDate} onChange={(e) => setSheetDate(e.target.value)}
                 className="h-10 rounded-xl mt-1 bg-white" data-testid="slot-filter-date"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Slot</label>
          <select value={slot} onChange={(e) => setSlot(e.target.value)}
                  className="h-10 px-3 rounded-xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="slot-filter-slot">
            <option value="morning">Morning</option>
            <option value="evening">Evening</option>
            <option value="afternoon">Afternoon</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="h-10 px-3 rounded-xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="slot-filter-status">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="out_for_delivery">Out for delivery</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Customer</label>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                  className="h-10 px-3 rounded-xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="slot-filter-customer">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Product</label>
          <select value={productId} onChange={(e) => setProductId(e.target.value)}
                  className="h-10 px-3 rounded-xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="slot-filter-product">
            <option value="">All products</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Preview table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm" data-testid="slot-report-table">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Qty</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-center px-4 py-3">Payment</th>
              <th className="text-center px-4 py-3">Delivery</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {busy && <tr><td colSpan={8}><LoadingState size="sm" /></td></tr>}
            {!busy && data.rows.length === 0 && (
              <tr><td colSpan={8}><EmptyState icon={Package} size="sm" title="No orders for this filter" /></td></tr>
            )}
            {!busy && data.rows.map((r) => {
              // Cancelled rows stay listed but are not in the Total (the
              // backend sums them separately) — struck through to match.
              // Under an explicit Cancelled filter they ARE the total.
              const excluded = r.delivery_status === "cancelled" && status !== "cancelled";
              return (
              <tr key={r.id} data-testid={`slot-row-${r.id}`} className="hover:bg-neutral-50">
                <td className="px-4 py-3 font-mono text-xs">#{r.order_id_short}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.customer_name}</div>
                  <div className="text-xs text-neutral-500 truncate max-w-[220px]">{r.address}</div>
                </td>
                <td className="px-4 py-3 text-xs">{r.customer_phone}</td>
                <td className="px-4 py-3">{r.product_name}</td>
                <td className={`px-4 py-3 text-right font-mono ${excluded ? "line-through text-neutral-400" : ""}`}>{r.quantity}</td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${excluded ? "line-through text-neutral-400" : ""}`}>{formatINR(r.amount)}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                    r.payment_status === "Paid" ? "bg-green-100 text-green-700" :
                    r.payment_status === "Refunded" ? "bg-blue-100 text-blue-700" :
                    r.payment_status === "Refund in progress" ? "bg-blue-50 text-blue-600" :
                    r.payment_status === "Paid (refund due)" || r.payment_status === "Refund failed" ? "bg-red-100 text-red-700" :
                    r.payment_status === "Not charged" ? "bg-neutral-100 text-neutral-600" :
                    "bg-yellow-100 text-yellow-700"
                  }`}>{r.payment_status}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                    r.delivery_status === "delivered" ? "bg-green-100 text-green-700" :
                    r.delivery_status === "failed" ? "bg-red-100 text-red-700" :
                    r.delivery_status === "skipped" ? "bg-neutral-100 text-neutral-600" :
                    r.delivery_status === "cancelled" ? "bg-orange-100 text-orange-800 line-through" :
                    "bg-blue-100 text-blue-700"
                  }`} data-testid={`slot-row-status-${r.id}`}>{r.delivery_status_label}</span>
                </td>
              </tr>
              );
            })}
          </tbody>
          {!busy && data.rows.length > 0 && (
            <tfoot className="bg-neutral-50 border-t-2 border-neutral-200">
              <tr data-testid="slot-report-total">
                <td colSpan={4} className="px-4 py-3 text-right font-bold uppercase tracking-wider text-xs text-neutral-500">Total</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{data.total_quantity}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(data.total_amount)}</td>
                <td colSpan={2}></td>
              </tr>
              {(data.cancelled_count || 0) > 0 && (
                <tr className="text-neutral-500" data-testid="slot-report-cancelled">
                  <td colSpan={4} className="px-4 py-2 text-right font-bold uppercase tracking-wider text-xs">
                    Cancelled (excluded) · {data.cancelled_count}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{data.cancelled_quantity}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatINR(data.cancelled_amount)}</td>
                  <td colSpan={2}></td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}


// ============ RIDER DAILY REPORT — preview table + Excel export ============
function RiderDailyReport({ sheetDate, setSheetDate }) {
  const [riderId, setRiderId] = useState("");
  const [riders, setRiders] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/admin/delivery-boys").then(({ data }) => setRiders(data.delivery_boys || data.riders || []))
       .catch(() => setRiders([]));
  }, []);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ date: sheetDate });
      if (riderId) params.append("rider_id", riderId);
      const { data } = await api.get(`/api/admin/reports/rider-daily?${params}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [sheetDate, riderId]);

  const exportXlsx = () => {
    const params = new URLSearchParams({ date: sheetDate });
    if (riderId) params.append("rider_id", riderId);
    const url = `${BACKEND_URL}/api/admin/reports/rider-daily.xlsx?${params}`;
    downloadFromApi(url, `rider_daily_${sheetDate}${riderId ? `_${riderId}` : ""}.xlsx`);
  };

  // Assigned excludes cancelled rows; those are their own column.
  const totals = rows.reduce((acc, r) => ({
    assigned: acc.assigned + r.assigned,
    delivered: acc.delivered + r.delivered,
    pending: acc.pending + r.pending,
    failed: acc.failed + r.failed,
    skipped: acc.skipped + r.skipped,
    cancelled: acc.cancelled + (r.cancelled || 0),
    collections: acc.collections + r.collections,
  }), { assigned: 0, delivered: 0, pending: 0, failed: 0, skipped: 0, cancelled: 0, collections: 0 });

  return (
    <section className="bg-white rounded-3xl border border-neutral-200 overflow-hidden" data-testid="rider-report-section">
      <div className="px-6 py-4 border-b border-neutral-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold">Delivery boy daily work status</h3>
          <p className="text-xs text-neutral-500">Per-rider summary — click Export for a detailed multi-sheet Excel.</p>
        </div>
        <Button onClick={exportXlsx} disabled={busy || rows.length === 0}
                className="h-10 rounded-2xl bg-brand-leaf hover:bg-green-700 font-bold px-6"
                data-testid="export-rider-xlsx">
          Export to Excel
        </Button>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3 border-b border-neutral-100 bg-neutral-50">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Date</label>
          <Input type="date" value={sheetDate} onChange={(e) => setSheetDate(e.target.value)}
                 className="h-10 rounded-xl mt-1 bg-white" data-testid="rider-filter-date"/>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Rider</label>
          <select value={riderId} onChange={(e) => setRiderId(e.target.value)}
                  className="h-10 px-3 rounded-xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="rider-filter-rider">
            <option value="">All riders</option>
            {riders.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.phone}</option>)}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm" data-testid="rider-report-table">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3">Rider</th>
              <th className="text-right px-4 py-3">Assigned</th>
              <th className="text-right px-4 py-3">Delivered</th>
              <th className="text-right px-4 py-3">Pending</th>
              <th className="text-right px-4 py-3">Failed</th>
              <th className="text-right px-4 py-3">Skipped</th>
              <th className="text-right px-4 py-3">Cancelled</th>
              <th className="text-right px-4 py-3">Collections</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {busy && <tr><td colSpan={8}><LoadingState size="sm" /></td></tr>}
            {!busy && rows.length === 0 && (
              <tr><td colSpan={8}><EmptyState icon={Users} size="sm" title="No data" description="No riders match this filter." /></td></tr>
            )}
            {!busy && rows.map((r) => (
              <tr key={r.rider_id} data-testid={`rider-row-${r.rider_id}`} className="hover:bg-neutral-50">
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.rider_name}</div>
                  <div className="text-xs text-neutral-500">{r.rider_phone}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.assigned}</td>
                <td className="px-4 py-3 text-right font-mono text-green-700 font-bold">{r.delivered}</td>
                <td className="px-4 py-3 text-right font-mono text-blue-700">{r.pending}</td>
                <td className="px-4 py-3 text-right font-mono text-red-700">{r.failed}</td>
                <td className="px-4 py-3 text-right font-mono text-neutral-500">{r.skipped}</td>
                <td className="px-4 py-3 text-right font-mono text-orange-800" data-testid={`rider-row-cancelled-${r.rider_id}`}>{r.cancelled || 0}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(r.collections)}</td>
              </tr>
            ))}
          </tbody>
          {!busy && rows.length > 0 && (
            <tfoot className="bg-neutral-50 border-t-2 border-neutral-200">
              <tr>
                <td className="px-4 py-3 font-bold uppercase tracking-wider text-xs text-neutral-500">Total</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{totals.assigned}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{totals.delivered}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{totals.pending}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{totals.failed}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{totals.skipped}</td>
                <td className="px-4 py-3 text-right font-mono font-bold" data-testid="rider-total-cancelled">{totals.cancelled}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatINR(totals.collections)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}


function CustomerBillingSection({ sheetDate }) {
  const [customers, setCustomers] = useState([]);
  const [selCustomer, setSelCustomer] = useState("");
  const [cycle, setCycle] = useState("weekly");
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);

  // Inclusive period ending on the chosen date — pure calendar arithmetic on
  // the YYYY-MM-DD string, so the device's time zone can't shift either end.
  const computePeriod = () => ({
    start: addDaysISO(sheetDate, cycle === "weekly" ? -6 : -29),
    end: sheetDate,
  });

  // Every customer, not just the first page: the list endpoint is paginated
  // (20 by default), which left most customers impossible to pick here.
  const loadCustomers = async () => {
    try {
      const all = [];
      for (let page = 1; page <= 50; page += 1) {
        const { data } = await api.get(`/api/admin/customers?page=${page}&page_size=100`);
        all.push(...(data.customers || []));
        if (!data.customers?.length || all.length >= (data.total ?? 0)) break;
      }
      setCustomers(all);
    } catch (e) { toast.error(friendlyMessage(e)); }
  };
  const loadInvoices = () => api.get("/api/admin/reports/invoices").then(({ data }) => setInvoices(data.invoices || [])).catch((e) => toast.error(friendlyMessage(e)));
  useEffect(() => { loadCustomers(); loadInvoices(); }, []);

  // Picking a customer narrows the list to their bills (every one of them);
  // otherwise it is the latest 25 across all customers.
  const shownInvoices = selCustomer
    ? invoices.filter((inv) => inv.customer_id === selCustomer)
    : invoices.slice(0, 25);

  const downloadOne = () => {
    if (!selCustomer) { toast.error("Pick a customer"); return; }
    const { start, end } = computePeriod();
    const cust = customers.find((c) => c.id === selCustomer);
    downloadCustomerBill(selCustomer, start, end, cust?.name).then(loadInvoices);
  };

  const generateBatch = async () => {
    const { start, end } = computePeriod();
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/reports/bills/generate", { start, end });
      const updated = data.invoices_updated ? `, updated ${data.invoices_updated}` : "";
      toast.success(`Generated ${data.invoices_created} invoice(s)${updated} for ${start} → ${end}`);
      loadInvoices();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="billing-section">
      <h3 className="font-heading font-bold mb-1">Customer invoices (Weekly / Monthly PDF)</h3>
      <p className="text-sm text-neutral-600 mb-4">
        Generate a per-customer PDF invoice for the {cycle} period ending on the chosen date.
        The batch action creates a summary record for every customer with deliveries in the window.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Billing cycle</label>
          <select value={cycle} onChange={(e) => setCycle(e.target.value)}
                  className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="billing-cycle">
            <option value="weekly">Weekly (last 7 days)</option>
            <option value="monthly">Monthly (last 30 days)</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Customer</label>
          <select value={selCustomer} onChange={(e) => setSelCustomer(e.target.value)}
                  className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1"
                  data-testid="billing-customer-select">
            <option value="">Choose customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>
            ))}
          </select>
        </div>
        <Button onClick={downloadOne} disabled={!selCustomer}
                className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold mt-6"
                data-testid="download-bill-btn">
          Download invoice .pdf
        </Button>
        <Button onClick={generateBatch} disabled={busy}
                className="h-11 rounded-2xl bg-brand-leaf hover:bg-green-700 font-bold mt-6"
                data-testid="generate-batch-bills-btn">
          {busy ? "Generating…" : "Bulk-generate for all"}
        </Button>
      </div>

      {invoices.length > 0 && (
        <div className="mt-4 border-t border-neutral-100 pt-4">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2" data-testid="invoices-heading">
            {selCustomer ? `Invoices for this customer (${shownInvoices.length})` : `Recent invoices (${invoices.length})`}
          </div>
          {/* Customer and period share one cell so the name stays on screen
              on a phone — as separate columns the table scrolled sideways and
              the rows read as unlabelled totals. */}
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="text-xs uppercase tracking-wider font-bold text-neutral-500">
              <tr className="border-b border-neutral-100">
                <th className="text-left py-2">Customer · Period</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {shownInvoices.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="py-2 pr-2">
                    <div className="font-semibold break-words" data-testid={`invoice-customer-${inv.id}`}>{inv.customer_name}</div>
                    <div className="text-xs text-neutral-500">{inv.period_start} → {inv.period_end}</div>
                  </td>
                  <td className="py-2 text-right font-mono font-bold whitespace-nowrap" data-testid={`invoice-total-${inv.id}`}>{formatINR(inv.total)}</td>
                  <td className="py-2 pl-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => downloadInvoice(inv)}
                      className="text-brand-sky font-bold text-xs hover:underline"
                      data-testid={`redownload-${inv.id}`}
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {selCustomer && shownInvoices.length === 0 && (
            <div className="text-sm text-neutral-500 py-3" data-testid="invoices-none-for-customer">No invoices for this customer yet.</div>
          )}
          {!selCustomer && invoices.length > shownInvoices.length && (
            <div className="text-xs text-neutral-500 pt-2" data-testid="invoices-more-hint">
              Showing the latest {shownInvoices.length}. Choose a customer above to see only their invoices.
            </div>
          )}
        </div>
      )}
    </section>
  );
}


// ============ CUSTOMER DISCOUNTS (per-customer product pricing) ============
export function AdminCustomerDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  // The chosen customer {id, name, phone}. Customers are NOT loaded here —
  // CustomerPicker searches them a page at a time (the old dropdown was
  // filled from the paginated customer list and so only held 20 of them).
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewFor, setPreviewFor] = useState(null); // discount row being previewed
  const [preview, setPreview] = useState(null);        // { customer, products, ... }
  const [form, setForm] = useState({
    customer_id: "", product_id: "*", discount_type: "percent",
    discount_value: 10, expires_at: "",
  });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const [d, p] = await Promise.all([
        api.get("/api/admin/customer-discounts"),
        api.get("/api/admin/products"),
      ]);
      setDiscounts(d.data.discounts || []);
      setProducts(p.data.products || []);
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    if (form.discount_value === "" || Number(form.discount_value) < 0) {
      toast.error("Enter a valid discount value"); return;
    }
    setBusy(true);
    try {
      await api.post("/api/admin/customer-discounts", {
        ...form,
        discount_value: Number(form.discount_value),
        expires_at: form.expires_at || null,
      });
      toast.success("Discount saved");
      setCustomer(null);
      setForm({ customer_id: "", product_id: "*", discount_type: "percent", discount_value: 10, expires_at: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (d) => {
    await api.patch(`/api/admin/customer-discounts/${d.id}`, { is_active: !d.is_active });
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete discount for ${d.customer_name}?`)) return;
    await api.delete(`/api/admin/customer-discounts/${d.id}`);
    toast.success("Deleted");
    load();
  };

  const openPreview = async (d) => {
    setPreviewFor(d);
    setPreview(null);
    try {
      const { data } = await api.get(`/api/admin/customer-discounts/preview?customer_id=${d.customer_id}`);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
      setPreviewFor(null);
    }
  };

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load discounts" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-discounts-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Pricing</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Customer-level discounts</h1>
        <p className="text-sm text-neutral-600 mt-1">Give specific customers a percentage or flat-amount discount on all products or a specific SKU. Applied automatically at checkout and subscription pricing.</p>
      </div>

      <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="discount-create-section">
        <h3 className="font-heading font-bold mb-4">Assign a new discount</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Customer</label>
            <CustomerPicker value={customer} testId="discount-customer"
                            onChange={(c) => { setCustomer(c); setForm((f) => ({ ...f, customer_id: c?.id || "" })); }} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Product</label>
            <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                    className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1"
                    data-testid="discount-product">
              <option value="*">All products</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Type</label>
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="h-11 px-4 rounded-2xl border border-neutral-200 bg-white w-full mt-1"
                    data-testid="discount-type">
              <option value="percent">% Percentage</option>
              <option value="fixed">₹ Fixed amount</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
              Value ({form.discount_type === "percent" ? "%" : "₹"})
            </label>
            <Input type="number" min="0" step="0.5" value={form.discount_value}
                   onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                   className="h-11 rounded-2xl mt-1" data-testid="discount-value"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Expires (optional)</label>
            <Input type="date" value={form.expires_at}
                   onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                   className="h-11 rounded-2xl mt-1" data-testid="discount-expires"/>
          </div>
        </div>
        <Button onClick={create} disabled={busy}
                className="h-11 mt-4 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold px-8"
                data-testid="save-discount-btn">
          {busy ? "Saving…" : "Save discount"}
        </Button>
      </section>

      <section className="bg-white rounded-3xl border border-neutral-200 overflow-hidden" data-testid="discount-list-section">
        <div className="px-6 py-4 border-b border-neutral-100">
          <h3 className="font-heading font-bold">Existing discounts ({discounts.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="text-left px-5 py-3">Customer</th>
                <th className="text-left px-5 py-3">Product</th>
                <th className="text-right px-5 py-3">Discount</th>
                <th className="text-center px-5 py-3">Expires</th>
                <th className="text-center px-5 py-3">Active</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {discounts.map((d) => (
                <tr key={d.id} data-testid={`discount-row-${d.id}`} className="hover:bg-neutral-50">
                  <td className="px-5 py-3">
                    <div className="font-semibold">{d.customer_name}</div>
                    <div className="text-xs text-neutral-500">{d.customer_phone}</div>
                  </td>
                  <td className="px-5 py-3 text-sm">{d.product_name}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold">
                    {d.discount_type === "percent" ? `${d.discount_value}%` : `₹${d.discount_value}`}
                  </td>
                  <td className="px-5 py-3 text-center text-xs">{d.expires_at || "—"}</td>
                  <td className="px-5 py-3 text-center">
                    <button onClick={() => toggle(d)}
                            className={`text-xs font-bold ${d.is_active ? "text-brand-leaf" : "text-neutral-400"}`}
                            data-testid={`toggle-${d.id}`}>
                      {d.is_active ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => openPreview(d)} className="text-brand-sky text-xs font-bold hover:underline mr-3"
                            data-testid={`verify-${d.id}`}>Verify</button>
                    <button onClick={() => remove(d)} className="text-brand-clay text-xs font-bold hover:underline"
                            data-testid={`delete-${d.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
              {discounts.length === 0 && (
                <tr><td colSpan={6}><EmptyState icon={Percent} size="sm" title="No discounts yet" description="Assign one above." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Effective-price preview dialog — used by admins to VERIFY the
          discount is being applied for a specific customer. Compares MRP
          vs the effective per-unit price returned by the same helper the
          checkout / subscription-pricing flow calls. */}
      <Dialog open={!!previewFor} onOpenChange={(o) => { if (!o) { setPreviewFor(null); setPreview(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Effective prices for {previewFor?.customer_name || "customer"}
            </DialogTitle>
          </DialogHeader>
          {!preview ? (
            <LoadingState />
          ) : (
            <div>
              <div className="mb-3 text-sm text-neutral-600">
                Below is what <span className="font-bold">{preview.customer.name}</span> ({preview.customer.phone}) will be charged.
                {preview.any_discount_active
                  ? <span className="text-green-700 font-bold ml-1">Discount is active.</span>
                  : <span className="text-neutral-400 ml-1">No active discount matched — check expiry / active toggle.</span>}
              </div>
              <div className="max-h-[420px] overflow-y-auto border border-neutral-200 rounded-2xl">
                <table className="w-full text-sm" data-testid="preview-price-table">
                  <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Product</th>
                      <th className="text-right px-3 py-2">MRP</th>
                      <th className="text-right px-3 py-2">Effective</th>
                      <th className="text-right px-3 py-2">You save</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {preview.products.map((p) => (
                      <tr key={p.product_id} className={p.has_discount ? "bg-green-50/50" : ""}>
                        <td className="px-3 py-2">{p.product_name}</td>
                        <td className="px-3 py-2 text-right font-mono text-neutral-500 line-through">₹{p.mrp}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">₹{p.effective_price}</td>
                        <td className="px-3 py-2 text-right font-mono text-green-700 font-bold">
                          {p.has_discount ? `−₹${p.savings}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-xs text-neutral-500">
                Note: These prices are recalculated live using the same logic that runs at checkout and subscription-creation, so what you see here is exactly what the customer is charged.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
